import React from 'react';

/* ══════════════════════════════════════════════════════════════
   ERROR BOUNDARY

   Without one of these, a single throw during render unmounts the
   whole React tree and leaves a blank white page — no message, no way
   back, and in production not even a hint of what happened. Two of
   the bugs found while building this app did exactly that.

   Must be a class: there is no hook equivalent of componentDidCatch.

   It deliberately does not try to be clever. Rendering is already
   known to be broken, so this uses inline styles rather than the
   stylesheet and plain anchors rather than the router — both of which
   could be part of what failed.
══════════════════════════════════════════════════════════════ */

/* The mark's outline, copied from KitsuMark rather than imported — see the
   note at the render site for why this file takes on that duplication. */
const BLOB = `M 193 0 C 168 0, 151 12, 138 35 C 121 64, 99 88, 69 106
  C 40 124, 13 143, 5 169 C -4 198, 5 224, 28 244 C 45 260, 70 271, 91 291
  C 111 310, 126 337, 143 359 C 157 378, 175 386, 193 386
  C 217 386, 235 374, 249 353 C 264 330, 279 309, 301 292
  C 321 277, 348 265, 365 246 C 383 226, 389 201, 381 174
  C 373 146, 350 126, 321 108 C 293 91, 270 67, 253 38
  C 240 15, 221 0, 193 0 Z`;

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the stack somewhere a person can actually find it.
    console.error('Unhandled render error:', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isDev = process.env.NODE_ENV === 'development';

    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, background: '#10191d', color: '#f7f3ea',
        fontFamily: "'Nunito', system-ui, sans-serif",
      }}>
        <div style={{
          width: '100%', maxWidth: 460, textAlign: 'center',
          padding: '38px 30px', borderRadius: 18,
          background: '#172228', border: '1px solid rgba(255,255,255,0.1)',
        }}>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
            {/* The mark, inlined rather than imported.

                KitsuMark is a real component — hooks, effects, a shared
                pointer listener, an idle animation loop. Rendering it here
                would mean the boundary depends on the same machinery that
                may have just thrown, and if the mark itself were ever the
                thing that failed, the boundary would fail with it and we
                would be back to a blank page. Same reason this file uses
                inline styles and plain anchors: nothing in the fallback
                may rely on anything that could be broken.

                So this is a static copy of the blob — no imports, no
                state, no animation. It only has to sit there and be the
                right shape. If the mark's outline ever changes, this is a
                second place to update; that is the deliberate cost. */}
            <svg width="72" height="72" viewBox="-8 -8 402 402" xmlns="http://www.w3.org/2000/svg"
                 role="img" aria-label="KitsuSpeak">
              <defs>
                <linearGradient id="kitsu-eb-grad" x1="193" y1="0" x2="193" y2="386"
                                gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#FBBF24" />
                  <stop offset="30%" stopColor="#FB923C" />
                  <stop offset="65%" stopColor="#F97316" />
                  <stop offset="100%" stopColor="#DC4C0A" />
                </linearGradient>
              </defs>
              <path fill="url(#kitsu-eb-grad)" d={BLOB} />
              {/* The eye, sad — it is an error screen. */}
              <rect x="125" y="125" width="136" height="136" rx="38" fill="#f7f3ea" />
              <path d="M148 178 C167 222, 219 222, 238 178" stroke="#0d1418"
                    strokeWidth="27" fill="none" strokeLinecap="round" />
            </svg>
          </div>

          <h1 style={{ fontSize: 20, fontWeight: 900, marginBottom: 9 }}>
            Something went wrong on this page
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(247,243,234,0.55)', marginBottom: 24 }}>
            Your progress is saved — this is only the screen failing to draw.
            Reloading usually clears it.
          </p>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 22px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: '#f97316', color: '#fff', fontWeight: 800, fontSize: 14,
                fontFamily: 'inherit',
              }}
            >
              Reload the page
            </button>
            {/* A plain anchor, not the router: the router may be what broke. */}
            <a
              href="/dashboard"
              style={{
                padding: '12px 22px', borderRadius: 10, textDecoration: 'none',
                border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(247,243,234,0.75)',
                fontWeight: 800, fontSize: 14,
              }}
            >
              Back to learning
            </a>
          </div>

          {isDev && (
            <pre style={{
              marginTop: 22, padding: 13, borderRadius: 10, textAlign: 'left',
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.24)',
              color: '#fca5a5', fontSize: 11.5, lineHeight: 1.5,
              whiteSpace: 'pre-wrap', overflowX: 'auto', maxHeight: 220,
            }}>
              {error.toString()}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
